var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { CustomElement, Attribute, OnEvent } from '../../utils/CustomElement.js';
import { Observable } from '../../../../Observable.js';
import { TabController } from './components/TabController.js';
import { ElementController } from '../ElementController.js';
import { Bound } from '../../../Bound.js';
import { IVector2 } from '../../../../api.js';
import { assert } from '../../../../babylon/utility/Assert.js';
import { clamp } from '../../../../Math.js';
export var TabType;
(function (TabType) {
    TabType["SELECTION"] = "selection";
    TabType["PERSISTENT"] = "persistent";
    TabType["CONFIG"] = "config";
})(TabType || (TabType = {}));
const DEBUG_MENU = 'debug-menu';
let DebugMenu = class DebugMenu extends HTMLElement {
    constructor() {
        super();
        this._active_tab_content_container = document.createElement('div');
        this._tabs_buttons_container = document.createElement('div');
        this._active_tab = null;
        this._tabs = {
            [TabType.SELECTION]: { container: document.createElement('div'), controllers: new Map() },
            [TabType.PERSISTENT]: { container: document.createElement('div'), controllers: new Map() },
            [TabType.CONFIG]: { container: document.createElement('div'), controllers: new Map() }
        };
        this._drag_start_position = new IVector2();
        this.x = '10';
        this.y = '80';
        this.update_observable = new Observable();
        this.selection = {
            get_tab: (tag) => this.get_or_create_tab(tag, TabType.SELECTION),
            clear: () => {
                const storage = this._tabs[TabType.SELECTION];
                storage.controllers.forEach(tab => tab.remove());
                storage.controllers.clear();
                storage.container.innerHTML = '';
            }
        };
        this.persistent = {
            get_tab: (tag) => this.get_or_create_tab(tag, TabType.PERSISTENT)
        };
        this.config = {
            get_tab: (tag) => this.get_or_create_tab(tag, TabType.CONFIG)
        };
        this._shadow = this.attachShadow({ mode: 'open' });
        this._tabs[TabType.SELECTION].container.classList.add('selection-tabs-container');
        this._tabs[TabType.PERSISTENT].container.classList.add('persistent-tabs-container');
        this._tabs[TabType.CONFIG].container.classList.add('config-tabs-container');
        this._active_tab_content_container.classList.add('tab-content');
        this._tabs_buttons_container.classList.add('tabs-container');
        this._tabs_buttons_container.appendChild(this._tabs[TabType.SELECTION].container);
        this._tabs_buttons_container.appendChild(this._tabs[TabType.PERSISTENT].container);
        this._tabs_buttons_container.appendChild(this._tabs[TabType.CONFIG].container);
        this.render();
    }
    drag_start(e) {
        this._drag_start_position.x = e.clientX;
        this._drag_start_position.y = e.clientY;
        window.addEventListener('pointermove', this.drag);
        window.addEventListener('pointerup', (e) => {
            const { x, y } = this.get_drag_position(e);
            this.x = String(x);
            this.y = String(y);
            window.removeEventListener('pointermove', this.drag);
        }, { once: true });
    }
    get_drag_position(e) {
        const { x, y } = this._drag_start_position;
        const delta_x = e.clientX - x;
        const delta_y = e.clientY - y;
        const parent = this.parentElement;
        assert(parent, 'Should have parent');
        return new IVector2(clamp(Number(this.x) - delta_x, 0, parent.offsetWidth - 40), clamp(Number(this.y) + delta_y, 20, parent.offsetHeight - 20));
    }
    drag(e) {
        const { x, y } = this.get_drag_position(e);
        this.style.right = `${x}px`;
        this.style.top = `${y}px`;
    }
    render() {
        this._shadow.innerHTML =
            /* html */
            `
      <style>
        :host {
          display: flex;
          gap:4px;
          position: absolute;
          z-index: 9001;
          right: ${this.x}px;
          top: ${this.y}px;
          min-width: 300px;
          padding: 10px;
          box-sizing: border-box;
          align-items: start;
          justify-content: center;
          pointer-events: none;
          height: calc(100% - ${this.y}px)
        }
        .container {
          display: flex;
          flex-direction: column;
          align-self: normal;
          align-items: center;
        }
        .container .container {
          margin-left: 8px;
        }
        .container-content {
          display: flex;
          flex-direction: column;
          width: 100%;
          align-items: center;
          padding-top: 4px;
        }
        .container-content > .container > .container-content {
          padding-bottom: 4px;
        }
        .container-content > div {
          border-radius: 0;
        }
        .container-content > button {
          border-radius: 0;
        }
        .container-content > div:nth-child(1 of .tool) {
          border-radius: 8px 8px 0 0;
        }
        .container-content > button:last-child, 
        .container-content > div:last-child, 
        .container-content > div:has(+ div[style*="display: none"]){
          border-radius:  0 0 8px 8px;
        }
        .container-content > div:nth-child(1 of .tool):nth-last-child(1 of .tool) {
          border-radius: 8px;
        }
        .tool {
          display: flex;
          width: 100%;
          align-items: center;
          justify-content: left;
          height: 24px;
          border-radius: 8px;
          background-color: #a5a5de35;
          position: relative;
        }
        .tool:hover {
          background-color: #a5a5de70;  
        }
        .checkbox {
          margin: 0 8px;
        }
        .string,
        .number {
          margin: 0 8px;
          max-width: 90px;

          font-size: 12px;
          height: 10px;
        }
        .number {
          cursor: e-resize;
        }
        .select,
        .color {
          margin: 0 8px;
          width: 98px;
          height: 18px;
          padding: 0px;
        }
        .container-label {
          align-self: flex-start;

          font-size: 14px;
        }
        .tab-content {
          display: flex;
          flex-direction: column;
          flex-grow: 1;

          height: calc(100% - 20px);
          overflow-y: auto;
        }
        .tab-content::-webkit-scrollbar {
          width: 4px;
          pointer-events: auto;
        }
        .tab-content::-webkit-scrollbar-thumb {
          background: #a5a5de35; 
          border-radius: 8px;
        }
        .tabs-container {
          display: flex;
          flex-direction: column;
          gap: 24px;
          align-self: flex-start;
          width: 22px;
          pointer-events: auto;

          max-height: calc(100% - 20px);
          overflow-y: auto;
          scrollbar-width: none;
        }
        .tabs-container::-webkit-scrollbar { 
          display: none;
        }
        .selection-tabs-container,
        .persistent-tabs-container,
        .config-tabs-container {
          display: flex;
          gap: 6px;
          flex-direction: column;
          width: 100%;
        }
        .tab {
          font-weight: lighter;
          font-size: 14px;
          background-color: #a5a5de20;
          border-radius: 8px;
          padding: 8px 3px;
          writing-mode: vertical-lr;
          text-align: center;
          letter-spacing: 0.4px;
          cursor: pointer;
        }
        .tab:hover {
          background-color: #a5a5dea3;
        }
        .tab-container {
          display: flex;
          flex-direction: column;
          padding: 10px;
          width: auto;
          gap: 4px;

          background-color: #80808000;
          border-radius: 8px;

          transition-property: background-color;
          transition-duration: 0.2s;
          transition-timing-function: ease-in-out;
          pointer-events: auto;
          font-size: 12px;
        }
        .tab-container:hover {
          background-color: #ffffff3d;
        }
        .tab-container .custom-label:not(:first-of-type) {
          margin-top: 16px;
        }
        .custom-label {
          margin-top: 0;
          margin-bottom: 4px;
          align-self: flex-start;
          font-size: 14px;
          font-weight: 500;
          color: #271a4d;
        }
        .custom-button {
          background-color: #a5a5dea3;
          justify-content: center;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 12px;
        }
        .custom-button:hover {
          background-color: #da4444a3;
        }
        .tracker {
          width: 8px;
          height: 8px;
          border-radius: 20px;
          background-color: #ff000000;
          position: absolute;
          right: 6px;
          cursor: pointer;
        }
        .tracker:hover {
          background-color: #000fff24;
        }
        .tracking-enabled {
          background-color: #ff0000d1;
        }
        .tool-bar {
          position: absolute;
          right: 10px;
          top: -16px;
          display: flex;
          gap: 10px;
        }
        .move-bar {
          color: #444;
          font-size: 14px;
          display: flex;
          width: 22px;
          height: 22px;
          box-sizing: border-box;
          align-items: center;
          justify-content: center;
          padding-bottom: 2px;
          border-radius: 6px;
          background-color: #a5a5de00;
          pointer-events: auto;
          cursor: move;
        }
        .move-bar:hover {
          background-color: #a5a5de20;
        }
      </style>
      <div class='tool-bar'>
        <div class='move-bar'>⚏</div>
      </div>
    `;
        this._shadow.appendChild(this._active_tab_content_container);
        this._shadow.appendChild(this._tabs_buttons_container);
    }
    update() {
        this.update_observable.notify(this);
    }
    set_active_tab(controller) {
        if (this._active_tab) {
            this._active_tab_content_container.removeChild(this._active_tab.elements.container);
        }
        if (controller) {
            this._active_tab_content_container.appendChild(controller.elements.container);
        }
        this._active_tab = controller;
    }
    get_active_tab() {
        var _a;
        return (_a = this._active_tab) !== null && _a !== void 0 ? _a : null;
    }
    remove_tab(controller) {
        controller.dispose();
        if (this._active_tab === controller) {
            this.set_active_tab(null);
        }
        try {
            this._tabs[controller.type].container.removeChild(controller.elements.button);
            this._tabs[controller.type].controllers.delete(controller.tag);
        }
        catch (_) {
            // Ignore if the tab is already removed
        }
    }
    get_or_create_tab(tag, type) {
        const { controllers, container } = this._tabs[type];
        const existing_controller = controllers.get(tag);
        if (existing_controller) {
            return existing_controller;
        }
        const elements = {
            button: this.create_tab_element(tag),
            container: this.create_tab_container(tag)
        };
        const controller = new TabController(tag, type, elements, this);
        elements.button.addEventListener('click', () => this.set_active_tab(controller));
        controllers.set(tag, controller);
        container.appendChild(elements.button);
        return controller;
    }
    create_tab_element(name) {
        const element = document.createElement('div');
        element.innerText = name;
        element.classList.add('tab');
        return element;
    }
    create_tab_container(name) {
        const element = document.createElement('div');
        element.classList.add('tab-container');
        return element;
    }
};
__decorate([
    Attribute
], DebugMenu.prototype, "x", void 0);
__decorate([
    Attribute
], DebugMenu.prototype, "y", void 0);
__decorate([
    OnEvent('pointerdown', '.move-bar')
], DebugMenu.prototype, "drag_start", null);
__decorate([
    Bound
], DebugMenu.prototype, "drag", null);
DebugMenu = __decorate([
    CustomElement(DEBUG_MENU)
], DebugMenu);
export { DebugMenu };
export class DebugMenuController extends ElementController {
    constructor(parent) {
        super(parent, DEBUG_MENU);
    }
}
//# sourceMappingURL=DebugMenu.js.map